import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { TERMS_MD, PRIVACY_MD, DPA_MD } from "./legalContent";

/**
 * Sprint G — /legal/terms, /legal/privacy, /legal/dpa
 * Renders one of three legal templates from legalContent.js as light Markdown.
 * Every page includes a prominent "template — attorney review required"
 * banner so visitors are never misled about the status of these documents.
 */

const DOCS = {
    terms:   { title: "Terms of Service",       body: TERMS_MD   },
    privacy: { title: "Privacy Policy",         body: PRIVACY_MD },
    dpa:     { title: "Data Processing Addendum", body: DPA_MD   },
};

export default function Legal() {
    const { doc } = useParams();
    const entry = DOCS[doc] || DOCS.terms;
    return (
        <div className="min-h-screen bg-[#fafaf7] text-gray-900" data-testid={`legal-page-${doc || "terms"}`}>
            <header className="border-b border-[#e8e8e3] bg-white">
                <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Nexus One
                    </Link>
                    <div className="flex gap-4 text-[13px]">
                        <Link to="/legal/terms" className={`hover:text-[#16a34a] ${doc === "terms" ? "text-[#16a34a] font-medium" : "text-gray-500"}`}>Terms</Link>
                        <Link to="/legal/privacy" className={`hover:text-[#16a34a] ${doc === "privacy" ? "text-[#16a34a] font-medium" : "text-gray-500"}`}>Privacy</Link>
                        <Link to="/legal/dpa" className={`hover:text-[#16a34a] ${doc === "dpa" ? "text-[#16a34a] font-medium" : "text-gray-500"}`}>DPA</Link>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-10">
                <div
                    className="mb-8 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4"
                    data-testid="legal-template-banner"
                >
                    <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <div className="text-[13.5px] text-amber-900 leading-relaxed">
                        <b>This is a template — have your attorney review before making it enforceable.</b>
                        {" "}Content below is influenced by widely-available public SaaS policies and is intentionally generic. Do not treat it as legal advice.
                    </div>
                </div>

                <h1 className="text-3xl font-bold tracking-tight text-gray-900 mb-2">{entry.title}</h1>
                <div className="text-xs text-gray-500 mb-8">
                    Last updated: {new Date().toISOString().slice(0, 10)}
                </div>

                <article
                    className="prose prose-sm max-w-none legal-prose"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(entry.body) }}
                />

                <div className="mt-14 pt-6 border-t border-[#e8e8e3] text-xs text-gray-500">
                    Questions or corrections? Reach us at{" "}
                    <a className="text-[#16a34a] font-medium" href="mailto:legal@nexus-one.com">legal@nexus-one.com</a>.
                </div>
            </main>

            <style>{`
                .legal-prose h2 { font-size: 1.15rem; font-weight: 700; margin-top: 1.75rem; margin-bottom: 0.6rem; color: #111827; }
                .legal-prose h3 { font-size: 1rem;    font-weight: 600; margin-top: 1.35rem; margin-bottom: 0.5rem; color: #111827; }
                .legal-prose p  { color: #374151; line-height: 1.7; margin-bottom: 0.85rem; font-size: 14.5px; }
                .legal-prose ul { list-style: disc; padding-left: 1.25rem; margin-bottom: 1rem; color: #374151; font-size: 14.5px; line-height: 1.7; }
                .legal-prose ol { list-style: decimal; padding-left: 1.25rem; margin-bottom: 1rem; color: #374151; font-size: 14.5px; line-height: 1.7; }
                .legal-prose li { margin-bottom: 0.25rem; }
                .legal-prose code { background: #f3f4f6; padding: 0.1rem 0.4rem; border-radius: 4px; font-size: 12.5px; }
                .legal-prose table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 13px; }
                .legal-prose th, .legal-prose td { border: 1px solid #e8e8e3; padding: 0.5rem 0.75rem; text-align: left; }
                .legal-prose th { background: #fafaf7; font-weight: 600; }
                .legal-prose b, .legal-prose strong { color: #111827; }
            `}</style>
        </div>
    );
}

/**
 * Very small markdown → HTML converter for these static templates.
 * Supports: headings (##, ###), paragraphs, lists (- ...), tables (|),
 * bold (**...**), inline code (`...`).
 */
function renderMarkdown(md) {
    const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const lines = md.split("\n");
    let html = "";
    let inList = false;
    let inTable = false;
    let tableRows = [];
    let paragraph = [];

    const flushParagraph = () => {
        if (paragraph.length) {
            html += `<p>${inlineMd(paragraph.join(" "))}</p>`;
            paragraph = [];
        }
    };
    const flushTable = () => {
        if (tableRows.length) {
            const [head, ...rest] = tableRows;
            html += "<table>";
            html += "<thead><tr>" + head.map((c) => `<th>${inlineMd(c)}</th>`).join("") + "</tr></thead>";
            html += "<tbody>";
            rest.forEach((row) => {
                if (row.every((c) => /^-+$/.test(c.trim()))) return;
                html += "<tr>" + row.map((c) => `<td>${inlineMd(c)}</td>`).join("") + "</tr>";
            });
            html += "</tbody></table>";
            tableRows = [];
            inTable = false;
        }
    };
    const inlineMd = (s) => {
        return escape(s)
            .replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>")
            .replace(/`([^`]+)`/g, "<code>$1</code>");
    };

    for (const raw of lines) {
        const line = raw.trimEnd();
        if (line.startsWith("|") && line.endsWith("|")) {
            flushParagraph();
            if (inList) { html += "</ul>"; inList = false; }
            inTable = true;
            const cells = line.slice(1, -1).split("|").map((c) => c.trim());
            tableRows.push(cells);
            continue;
        } else if (inTable) {
            flushTable();
        }
        if (line.startsWith("### ")) {
            flushParagraph();
            if (inList) { html += "</ul>"; inList = false; }
            html += `<h3>${inlineMd(line.slice(4))}</h3>`;
        } else if (line.startsWith("## ")) {
            flushParagraph();
            if (inList) { html += "</ul>"; inList = false; }
            html += `<h2>${inlineMd(line.slice(3))}</h2>`;
        } else if (line.startsWith("- ")) {
            flushParagraph();
            if (!inList) { html += "<ul>"; inList = true; }
            html += `<li>${inlineMd(line.slice(2))}</li>`;
        } else if (line === "") {
            flushParagraph();
            if (inList) { html += "</ul>"; inList = false; }
        } else {
            paragraph.push(line);
        }
    }
    flushParagraph();
    flushTable();
    if (inList) html += "</ul>";
    return html;
}
